/**
 * Branding selector — visual card picker showing logo images.
 * User scrolls through and clicks the one they want.
 */
import React from 'react';
import Card from '@amzn/meridian/card';
import Box from '@amzn/meridian/box';
import Column from '@amzn/meridian/column';
import Row from '@amzn/meridian/row';
import Text from '@amzn/meridian/text';
import { useTranslation } from 'src/arb-wrapper';
import { useS3BlobUrl } from 'src/hooks/useS3BlobUrl';

export interface BrandPreset {
  brandType: string;
  businessName: string;
  logoS3Key: string;
}

interface BrandingSelectorProps {
  presets: BrandPreset[];
  selectedBrandType?: string;
  onChange: (brandType: string) => void;
  mediaBucketName: string;
  disabled?: boolean;
}

const BrandCard: React.FC<{ preset: BrandPreset; mediaBucketName: string }> = ({ preset, mediaBucketName }) => {
  const logoUrl = useS3BlobUrl(mediaBucketName, preset.logoS3Key, { contentType: 'image/png' });

  return (
    <Column alignmentHorizontal="center" spacingInset="400">
      {logoUrl
        ? <img alt={`${preset.businessName} logo`} src={logoUrl} style={{ width: 80, height: 80, objectFit: 'contain' }} />
        : <div style={{ width: 80, height: 80, backgroundColor: '#f5f5f5', borderRadius: 4 }} />
      }
      <Text alignment="center" type="b200">{preset.businessName}</Text>
    </Column>
  );
};

export const BrandingSelector: React.FC<BrandingSelectorProps> = ({
  presets, selectedBrandType, onChange, mediaBucketName, disabled,
}) => {
  const { t } = useTranslation();

  return (
    <Column spacing="200">
      <Text color="secondary" type="b300">{t('publisher_config_brand_heading')}</Text>
      <Text color="secondary" type="b100">{t('publisher_config_brand_description')}</Text>
      <Box spacingInset="400" type="fill">
        <Row data-testid="brand-selector-scroll" spacing="400">
          {presets.map(preset => (
            <Card
              checked={preset.brandType === selectedBrandType}
              data-testid={`brand-card-${preset.brandType}`}
              disabled={disabled}
              key={preset.brandType}
              spacingInset="none"
              value={preset.brandType}
              width={160}
              onClick={onChange}
            >
              <BrandCard mediaBucketName={mediaBucketName} preset={preset} />
            </Card>
          ))}
        </Row>
      </Box>
    </Column>
  );
};
